import React, { useEffect, useState } from 'react';
import {
  SelectLabel,
  Container,
  Form,
  Footer,
} from './styles/StyledScaleFilter';
import { Select } from '../UI';
import { Button } from '@wisecare-tech/design-system-web';
import { useSelector } from 'react-redux';
import { iStore } from '~/domain/interfaces/models/Store';
import { ListMacroRegion } from '~/domain/usecases/macroRegion/remote';
import { ListSystemModule } from '~/domain/usecases/systemModule/remote';
import { listDaysOfTheWeek } from '~/utils/listDatesOfTheWeek';
import { makeReduxGetAllOrgUnitList } from '~/main/factories/usecases/orgUnit/OrgUnitListFactory';
import { makeReduxProfessionList } from '~/main/factories/usecases/profession';
import { makeRemoteListMacroRegion } from '~/main/factories/usecases/macroRegion/ListMacroRegionFactory';
import { AlertMessage } from '../messages/AlertMessage';
import { makeRemoteListSystemModule } from '~/main/factories/usecases/systemModule/ListSystemModuleFactory';
import { makeReduxGetAllSpecialty } from '~/main/factories/usecases/specialty/GetAllSpecialtyFactory';
import { makeReduxGetAllProfessionals } from '~/main/factories/usecases/professional/GetAllProfessionalFactory';
import { DaysInitials } from '~/domain/interfaces/models/TeleShifts';
import { useTeleShifts } from '~/presentation/hooks/teleShifts';

type FormData = {
  weekDay?: string;
  day?: string;
  unit?: string;
  macroregion?: string;
  profession?: string;
  specialty?: string;
  professional?: string;
};

type Data = {
  start: string;
  end: string;
  user?: number;
  orgUnit?: number;
  specialty?: number;
  profession?: number;
  macroregion?: number;
  day?: DaysInitials;
};

const dayOfWeekMap: { [key: string]: string } = {
  Domingo: 'SU',
  'Segunda-feira': 'MO',
  'Terça-feira': 'TU',
  'Quarta-feira': 'WE',
  'Quinta-feira': 'TH',
  'Sexta-feira': 'FR',
  Sábado: 'SA',
};

export const ScaleFilter: React.FC = () => {
  const { resetShiftsFilter, start, end } = useTeleShifts();
  const { results } = useSelector((store: iStore) => store.orgUnit);
  const { results: resultsProfession } = useSelector(
    (store: iStore) => store.professions,
  );
  const { results: resultsSpecialty } = useSelector(
    (store: iStore) => store.specialty,
  );
  const { results: resultsProfessionals } = useSelector(
    (store: iStore) => store.professional,
  );
  const { orgId, orgUnits, role } = useSelector(
    (store: iStore) => store.auth.selectUser,
  );

  const [macroRegion, setMacroRegion] =
    useState<ListMacroRegion.Model['records']>();

  const { setIsFilterActive, filterTeleShifts, filterTeleShiftsCallback } =
    useTeleShifts();

  const [filterData, setFilterData] = useState<FormData>({
    ...filterTeleShifts,
  });

  const handleApplyFilters = () => {
    if (filterData) {
      filterTeleShiftsCallback(filterData);
    }
    setIsFilterActive(true);
    document.getElementById('close_filter')?.click();
  };

  useEffect(() => {
    makeReduxGetAllOrgUnitList().list({
      filter: {
        enabled: true,
      },
    });

    makeReduxProfessionList().post({
      filter: { enabled: true, hasProfessional: true },
    });

    makeRemoteListMacroRegion()
      .list({})
      .then(response => {
        setMacroRegion(response.records);
      })
      .catch(() => {
        AlertMessage({
          message: 'Erro ao carregar as macrorregiões',
          type: 'danger',
        });
      });
  }, []);

  useEffect(() => {
    if (!filterData?.profession) return;

    makeReduxGetAllSpecialty().getAll({
      filter: {
        hasProfessional: true,
        enabled: true,
        profession: Number(filterData?.profession),
      },
    });

    setFilterData({
      ...filterData,
      specialty: undefined,
    });
  }, [filterData?.profession]);

  useEffect(() => {
    if (!filterData?.specialty) return;

    const orgUnitsFormatted =
      (orgUnits?.map(item => item.id).filter(Boolean) as number[]) ?? [];

    makeReduxGetAllProfessionals().getAll({
      filter: {
        enabled: true,
        specialty: Number(filterData?.specialty),
        situation: ['ACTIVE', '2FAWAIT'],
        org: orgId,
        unit: role === 'ORG' || role === 'PRO' ? orgUnitsFormatted : undefined,
        shiftSpecialty: Number(filterData?.specialty),
      },
    });

    setFilterData({
      ...filterData,
      professional: undefined,
    });
  }, [filterData?.specialty, orgUnits, role, orgId]);

  return (
    <Container>
      <Form>
        <div>
          <SelectLabel>Dia da semana</SelectLabel>

          <Select
            height="40px"
            defaultValue={filterData?.day}
            value={filterData?.weekDay ?? ''}
            onChange={e => {
              setFilterData({
                ...filterData,
                weekDay: e.target.value as DaysInitials,
              });
            }}
          >
            <option value="">Selecione</option>
            {Object.entries(dayOfWeekMap)?.map(([label, value]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <SelectLabel>Setor</SelectLabel>

          <Select
            height="40px"
            defaultValue={filterData?.unit}
            value={filterData?.unit ?? ''}
            onChange={e => {
              setFilterData({
                ...filterData,
                unit: e.target.value,
              });
            }}
          >
            <option value="">Selecione</option>
            {results?.map(item => (
              <option key={item.orgUnit?.id} value={String(item?.orgUnit?.id)}>
                {item.orgUnit?.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <SelectLabel>Macrorregião</SelectLabel>
          <Select
            height="40px"
            defaultValue={filterData?.macroregion}
            value={filterData?.macroregion ?? ''}
            onChange={e => {
              setFilterData({
                ...filterData,
                macroregion: e.target.value,
              });
            }}
          >
            <option value="">Selecione</option>
            {macroRegion?.map(item => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <SelectLabel>Profissão</SelectLabel>

          <Select
            height="40px"
            defaultValue={filterData?.profession ?? ''}
            value={filterData?.profession ?? ''}
            onChange={e => {
              setFilterData({
                ...filterData,
                profession: e.target.value,
              });
            }}
          >
            <option value="">Selecione</option>
            {resultsProfession?.map(item => (
              <option key={item?.profession?.id} value={item?.profession?.id}>
                {item?.profession?.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <SelectLabel>Especialidade</SelectLabel>

          <Select
            height="40px"
            defaultValue={filterData?.specialty}
            onChange={e => {
              setFilterData({
                ...filterData,
                specialty: e.target.value,
              });
            }}
          >
            <option value="">Selecione</option>
            {filterData?.profession && (
              <>
                {resultsSpecialty?.map(item => (
                  <option key={item?.specialty?.id} value={item?.specialty?.id}>
                    {item?.specialty?.name}
                  </option>
                ))}
              </>
            )}
          </Select>
        </div>
        <div>
          <SelectLabel>Profissional</SelectLabel>

          <Select
            height="40px"
            defaultValue={filterData?.professional}
            onChange={e => {
              setFilterData({
                ...filterData,
                professional: e.target.value,
              });
            }}
          >
            <option value="">Selecione</option>
            {filterData?.specialty && (
              <>
                {resultsProfessionals?.map(item => {
                  if (
                    item?.specialties?.find(
                      specialty =>
                        specialty?.id === Number(filterData?.specialty),
                    )
                  ) {
                    return (
                      <option
                        key={item?.professional?.id}
                        value={item?.user?.id}
                      >
                        {`${item?.user?.firstName} ${item?.user?.lastName}`}
                      </option>
                    );
                  }
                })}
              </>
            )}
          </Select>
        </div>
      </Form>
      <Footer>
        <Button
          variant="secondary"
          onClick={() => {
            resetShiftsFilter();
          }}
        >
          Restaurar padrões
        </Button>
        <Button variant="primary" onClick={handleApplyFilters}>
          Aplicar filtros
        </Button>
      </Footer>
    </Container>
  );
};
